import React, { useEffect, useState } from "react";
import api from "../lib/api";

export function CrudPage({ resource, title, columns, defaultBody, fields, listQuery = "", filterKey, filterLabel, headerExtra }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterVal, setFilterVal] = useState("");
  const load = async () => { const { data } = await api.get(`/${resource}${listQuery}`); setItems(data); };
  useEffect(() => { load(); }, []);
  const save = async () => {
    const body = { ...editing };
    delete body.id; delete body.created_at;
    if (editing.id) await api.put(`/${resource}/${editing.id}`, body);
    else await api.post(`/${resource}`, body);
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/${resource}/${id}`); load();
  };

  const filterOptions = filterKey
    ? [...new Set(items.map((i) => i[filterKey]).filter((v) => v != null && v !== ""))].sort((a, b) =>
        String(a).localeCompare(String(b))
      )
    : [];
  const displayed = (filterKey
    ? items
        .filter((i) => !filterVal || String(i[filterKey] ?? "") === filterVal)
        .slice()
        .sort((a, b) =>
          String(a[filterKey] ?? "").localeCompare(String(b[filterKey] ?? "")) ||
          String(a.job_code ?? a.code ?? "").localeCompare(String(b.job_code ?? b.code ?? ""))
        )
    : items);

  return (
    <div className="space-y-6" data-testid={`${resource}-page`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{title}</h1>
        <div className="flex items-end gap-3">
          {filterKey && (
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">{filterLabel || "Filter"}</label>
              <select
                data-testid={`${resource}-filter-${filterKey}`}
                value={filterVal}
                onChange={(e) => setFilterVal(e.target.value)}
                className="h-14 px-4 bg-white border border-stone-200 text-base font-medium min-w-[14rem]"
              >
                <option value="">All ({items.length})</option>
                {filterOptions.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          )}
          {headerExtra && headerExtra(load)}
          <button data-testid={`new-${resource}-btn`} onClick={() => setEditing({ ...defaultBody })}
            className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5">+ New</button>
        </div>
      </div>
      <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b-2 border-stone-300">
            <tr className="text-left tracking-wide text-xs">
              {columns.map((c) => (<th key={c.key} className="p-3">{c.label}</th>))}
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((row) => (
              <tr key={row.id} className="border-b border-stone-200" data-testid={`${resource}-row-${row.id}`}>
                {columns.map((c) => (<td key={c.key} className="p-3 font-mono">{String(row[c.key] ?? "")}</td>))}
                <td className="p-3 space-x-2">
                  <button onClick={() => setEditing(row)} className="px-3 h-9 border border-stone-200 font-bold uppercase text-xs hover:bg-[#D8C7A1]">Edit</button>
                  <button onClick={() => remove(row.id)} className="px-3 h-9 border border-stone-200 font-bold uppercase text-xs hover:bg-red-200">Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/10 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-black uppercase mb-4" style={{ fontFamily: "'Fraunces', serif" }}>{editing.id ? "Edit" : "New"} {title.slice(0, -1)}</h3>
            <div className="space-y-3">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">{f.label}</label>
                  {f.type === "select" ? (
                    <select value={editing[f.key] ?? ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="w-full h-12 px-3 border border-stone-200">
                      {f.options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  ) : f.type === "checkbox" ? (
                    <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} className="w-5 h-5" />
                  ) : (
                    <input
                      data-testid={`${resource}-${f.key}-input`}
                      type={f.type || "text"}
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value })}
                      className="w-full h-12 px-3 border border-stone-200"
                    />
                  )}
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <button data-testid={`save-${resource}-btn`} onClick={save} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5">Save</button>
                <button onClick={() => setEditing(null)} className="flex-1 h-12 bg-white font-bold uppercase border border-stone-200">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Employees() {
  return (
    <CrudPage
      resource="employees"
      title="Employees"
      defaultBody={{ name: "", employee_code: "", pay_rate: 0, category: "Crew Member", active: true }}
      columns={[
        { key: "name", label: "Name" },
        { key: "employee_code", label: "Code" },
        { key: "category", label: "Role" },
        { key: "pay_rate", label: "Pay Rate" },
        { key: "active", label: "Active" },
      ]}
      fields={[
        { key: "name", label: "Name" },
        { key: "employee_code", label: "Employee Code" },
        { key: "category", label: "Role", type: "select", options: [
          { value: "Admin", label: "Admin" },
          { value: "Supervisor", label: "Supervisor" },
          { value: "Foreman", label: "Foreman" },
          { value: "Crew Member", label: "Crew Member" },
          { value: "Equipment Operator", label: "Equipment Operator" },
          { value: "Irrigator", label: "Irrigator" },
        ] },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "pay_rate", label: "Pay Rate ($/hr)", type: "number" },
        { key: "active", label: "Active", type: "checkbox" },
      ]}
    />
  );
}

export function Clients() {
  return (
    <CrudPage
      resource="clients"
      title="Clients"
      defaultBody={{ name: "", contact_name: "", email: "", phone: "", notes: "", active: true }}
      columns={[
        { key: "name", label: "Client" },
        { key: "contact_name", label: "Contact" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "active", label: "Active" },
      ]}
      fields={[
        { key: "name", label: "Client Name" },
        { key: "contact_name", label: "Contact Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "notes", label: "Notes" },
        { key: "active", label: "Active", type: "checkbox" },
      ]}
    />
  );
}

export function JobCodes() {
  return (
    <CrudPage
      resource="jobcodes"
      title="Job Codes"
      filterKey="category"
      filterLabel="Filter by Category"
      defaultBody={{ category: "", category_code: "", job: "", job_code: "", requires_equipment: false, active: true }}
      columns={[
        { key: "category", label: "Category" },
        { key: "category_code", label: "Category Code" },
        { key: "job", label: "Job" },
        { key: "job_code", label: "Job Code" },
        { key: "active", label: "Active" },
      ]}
      fields={[
        { key: "category", label: "Category" },
        { key: "category_code", label: "Category Code" },
        { key: "job", label: "Job" },
        { key: "job_code", label: "Job Code" },
        { key: "requires_equipment", label: "Requires Equipment Selection", type: "checkbox" },
        { key: "active", label: "Active", type: "checkbox" },
      ]}
    />
  );
}

export function EquipmentPage() {
  const [items, setItems] = React.useState([]);
  const [editing, setEditing] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [cat, setCat] = React.useState("");

  const load = async () => { const { data } = await (await import("../lib/api")).default.get("/equipment"); setItems(data); };
  React.useEffect(() => { load(); }, []);

  const save = async () => {
    const api = (await import("../lib/api")).default;
    const body = { ...editing }; delete body.id;
    if (editing.id) await api.put(`/equipment/${editing.id}`, body);
    else await api.post("/equipment", body);
    setEditing(null); load();
  };
  const remove = async (id) => {
    const api = (await import("../lib/api")).default;
    if (!window.confirm("Delete?")) return;
    await api.delete(`/equipment/${id}`); load();
  };

  const categories = Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort();
  const filtered = items.filter((i) => {
    if (cat && i.category !== cat) return false;
    if (q) {
      const s = q.toLowerCase();
      return (i.name || "").toLowerCase().includes(s) ||
             (i.code || "").toLowerCase().includes(s) ||
             (i.category || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="equipment-page">
      <div className="flex items-end justify-between">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Equipment</h1>
        <button data-testid="new-equipment-btn" onClick={() => setEditing({ name: "", code: "", category: "", active: true })}
          className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5">+ New</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          data-testid="equipment-search"
          placeholder="Search by name, code, or category..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-12 px-4 border border-stone-200 text-base"
        />
        <select
          data-testid="equipment-category-filter"
          value={cat}
          onChange={(e) => setCat(e.target.value)}
          className="h-12 px-4 border border-stone-200"
        >
          <option value="">All Categories</option>
          {categories.map((c) => (<option key={c} value={c}>{c}</option>))}
        </select>
      </div>

      <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b-2 border-stone-300">
            <tr className="text-left tracking-wide text-xs">
              <th className="p-3">Name</th>
              <th className="p-3">Code</th>
              <th className="p-3">Category</th>
              <th className="p-3">Active</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (<tr><td colSpan={5} className="p-6 text-center text-stone-500 font-bold uppercase text-xs">No equipment</td></tr>)}
            {filtered.map((row) => (
              <tr key={row.id} className="border-b border-stone-200" data-testid={`equipment-row-${row.id}`}>
                <td className="p-3 font-bold">{row.name}</td>
                <td className="p-3 font-mono">{row.code}</td>
                <td className="p-3"><span className="inline-block bg-[#1F3D34] text-white text-xs font-bold uppercase px-2 py-1">{row.category || "—"}</span></td>
                <td className="p-3 font-mono">{String(row.active)}</td>
                <td className="p-3 space-x-2">
                  <button onClick={() => setEditing(row)} className="px-3 h-9 border border-stone-200 font-bold uppercase text-xs hover:bg-[#D8C7A1]">Edit</button>
                  <button onClick={() => remove(row.id)} className="px-3 h-9 border border-stone-200 font-bold uppercase text-xs hover:bg-red-200">Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/10 w-full max-w-lg p-6">
            <h3 className="text-2xl font-black uppercase mb-4">{editing.id ? "Edit" : "New"} Equipment</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold uppercase block mb-1">Name</label>
                <input data-testid="equipment-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full h-12 px-3 border border-stone-200" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase block mb-1">Equipment Number / Code</label>
                <input data-testid="equipment-code-input" value={editing.code || ""} onChange={(e) => setEditing({ ...editing, code: e.target.value })} className="w-full h-12 px-3 border border-stone-200" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase block mb-1">Category</label>
                <input data-testid="equipment-category-input" list="equip-cats" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full h-12 px-3 border border-stone-200" placeholder="e.g. Tractor, Heavy Equipment" />
                <datalist id="equip-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="w-5 h-5" /> Active</label>
              <div className="flex gap-2">
                <button data-testid="save-equipment-btn" onClick={save} className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200">Save</button>
                <button onClick={() => setEditing(null)} className="flex-1 h-12 bg-white font-bold uppercase border border-stone-200">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
